import styled from 'styled-components'
import { Layer } from '~/packages/general/variable/spaceTime.types'
import ViewSelectTopBoxDesktop from './viewSelectTopBoxDesktop.component'
import { VariableWithConclusion } from '~/packages/general/variable/variables.typing'
import useMedia from 'react-use/lib/useMedia'
import { ReportWithLayer } from './reports'
import { useRef, useState } from 'react'
import { useClickAway } from 'react-use'
import { ScreenDefault } from '~/components/baseInterface'

interface WrapperProps {
  $isOpen: boolean
}

const Wrapper = styled.aside<WrapperProps>`
  position: absolute;
  top: ${({ theme }) => theme.padding(0.6)};
  left: ${({ theme }) => theme.padding(0.6)};

  display: flex;
  flex-direction: column;

  min-height: 80px;
  max-height: 100px;
  max-width: 600px;
  width: 50%;

  .search-button-wrapper {
    button {
      display: flex;
      flex-direction: column;
      background: transparent;
      box-shadow: none;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      justify-content: center;
      align-items: center;
      transition-duration: ${({ theme }) => theme.transitionDurationMs}ms;
      transition-timing-function: ${({ theme }) => theme.transitionTimingFunction};
      transition-property: background-color, box-shadow;
      text-align: left;
      font-size: 1.2rem;
      color: ${({ theme }) => theme.colors.primary};

      padding: 10px;
      margin: 0 5px 5px 1px;

      background-color: #fff;

      &:hover {
        background-color: #fff;
        box-shadow: ${({ theme }) => theme.shadow(2)};
        z-index: 1;
      }
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    min-height: unset;
    width: 45px;
    height: 45px;
  }

  .layer {
    display: flex;
    flex: 1;
    border-radius: 8px;
    box-shadow: ${({ theme }) => theme.shadow(1)};
    margin-bottom: ${({ theme }) => theme.padding(0.3)};
    margin-bottom: calc(${({ theme }) => theme.padding(0.3)} + 1px);

    background-color: white;

    @supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
      background-color: ${({ theme }) => theme.backdropBackground};
      -webkit-backdrop-filter: ${({ theme }) => theme.backdropFilter};
      backdrop-filter: ${({ theme }) => theme.backdropFilter};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints?.mobile}px) {
      visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
      opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
      transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '-30%')});
      transition: transform 0.3s ease-in-out;
      width: 60%;
      min-width: 200px;
      z-index: 1;
      box-shadow: 4px 4px 8px #00000026;
    }
  }
`

interface ViewSelectProps {
  variables: VariableWithConclusion[]
  baseLayers: Layer[]
  layers: Layer[]
  setBaseLayers: (layers: Layer[] | ((layers: Layer[]) => Layer[])) => void
  onChangeReport: (report: ReportWithLayer | null) => void
  stateId: number | null
  stateName: string | null
  subtitle?: string | null
  isDisabled: boolean
}

export default function ViewSelect({
  baseLayers,
  layers,
  setBaseLayers,
  variables,
  onChangeReport,
  stateId,
  stateName,
  subtitle,
  isDisabled,
}: ViewSelectProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const ref = useRef(null)
  const isMobile = useMedia('(max-width: 768px)')

  useClickAway(ref, () => {
    if (isOpen) {
      setIsOpen(prevData => !prevData)
    }
  })

  return (
    <Wrapper ref={ref} $isOpen={isOpen}>
      {isMobile && (
        <div className='search-button-wrapper'>
          <ScreenDefault.Button isOpen={isOpen} onClick={e => setIsOpen(prevData => !prevData)} />
        </div>
      )}

      {layers.map((layer, index) => (
        <div className='layer' key={index}>
          <ViewSelectTopBoxDesktop
            variables={variables}
            layer={layer}
            baseLayer={baseLayers[index]}
            onChangeReport={onChangeReport}
            onChangeLayer={layer => {
              setBaseLayers(oldLayers => {
                const newLayers = [...layers]
                newLayers[index] = typeof layer === 'function' ? layer(oldLayers[index]) : layer
                return newLayers
              })
            }}
            stateId={stateId}
            stateName={stateName}
            subtitle={subtitle}
            isDisabled={isDisabled}
          />
        </div>
      ))}
    </Wrapper>
  )
}
